import { useEffect, useState } from 'react'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import type { Id, SemanticObjectSetResult } from '@/frontend/features/Fde/types'

export interface SemanticObjectFilter {
  property_key: string
  contains?: string
  oneOf?: Array<string | number>
  notIn?: Array<string | number>
  range?: [number, number]
  timeRange?: [number, number]
  greaterThan?: number
  greaterThanOrEqual?: number
  lessThan?: number
  lessThanOrEqual?: number
}

interface Options {
  projectId: Id
  classId: Id | null | undefined
  dataAssetId?: Id | null
  limit?: number
  enabled?: boolean
  filters?: SemanticObjectFilter[]
  errorLabel: string
}

export default function useSemanticObjects(options: Options) {
  const [result, setResult] = useState<SemanticObjectSetResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const filtersKey = JSON.stringify(options.filters || [])

  useEffect(() => {
    if (!options.enabled || !options.classId) return

    const controller = new AbortController()
    setResult(null)
    setLoading(true)
    setError(null)
    fetch(fdeProjectPath(fdeRoutes.semanticObjects, String(options.projectId)), {
      method: 'POST',
      credentials: 'same-origin',
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
      },
      body: JSON.stringify({
        class_id: options.classId,
        data_asset_id: options.dataAssetId || undefined,
        limit: options.limit || 10,
        filters: options.filters || [],
      }),
    })
      .then(async (response) => {
        const payload = await response.json()
        if (!response.ok) throw new Error(payload.error || options.errorLabel)
        return payload as SemanticObjectSetResult
      })
      .then(setResult)
      .catch((requestError: Error) => {
        if (requestError.name !== 'AbortError') setError(requestError.message)
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false) })

    return () => controller.abort()
  }, [options.classId, options.dataAssetId, options.enabled, options.errorLabel, options.limit, options.projectId, filtersKey])

  return { result, error, loading }
}
